// pages/monitoring/StatusDashboardPage.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContainerApps } from '../../store/slices/monitoringSlice';
import { RootState, AppDispatch } from '../../store';
import StatusCard from '../../components/monitoring/StatusCard';
import StatusBadge from '../../components/monitoring/StatusBadge';

const StatusDashboardPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { containerapps, alerts, loading, error } = useSelector((state: RootState) => state.monitoring);
    const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

    useEffect(() => {
        dispatch(fetchContainerApps());
        setLastRefreshed(new Date());

        // Set up polling interval
        const interval = setInterval(() => {
            dispatch(fetchContainerApps());
            setLastRefreshed(new Date());
        }, 60000); // Every minute

        return () => clearInterval(interval);
    }, [dispatch]);

    // Calculate summary statistics
    const totalApps = containerapps.length;
    const runningApps = containerapps.filter((app: { status: string; }) => app.status === 'running').length;
    const stoppedApps = containerapps.filter((app: { status: string; }) => app.status === 'stopped').length;
    const warningApps = containerapps.filter((app: { status: string; }) => app.status === 'warning').length;
    const healthPercentage = totalApps > 0 ? Math.round((runningApps / totalApps) * 100) : 0;

    // Critical alerts count
    const criticalAlerts = alerts.filter((alert: { severity: string; resolved: any; }) => alert.severity === 'critical' && !alert.resolved).length;

    if (loading && containerapps.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">System Status Dashboard</h1>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    Last updated: {lastRefreshed.toLocaleTimeString()}
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p>Error: {error}</p>
                </div>
            )}

            {/* Status Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatusCard
                    title="System Health"
                    value={`${healthPercentage}%`}
                    color={
                        healthPercentage >= 90 ? 'green' :
                            healthPercentage >= 70 ? 'yellow' : 'red'
                    }
                />
                <StatusCard
                    title="Active Containers"
                    value={`${runningApps}/${totalApps}`}
                    color="blue"
                />
                <StatusCard
                    title="Stopped Containers"
                    value={stoppedApps.toString()}
                    color={stoppedApps > 0 ? 'red' : 'green'}
                />
                <StatusCard
                    title="Critical Alerts"
                    value={criticalAlerts.toString()}
                    color={criticalAlerts > 0 ? 'red' : 'green'}
                />
            </div>

            {/* Status Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Service Status</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Service
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Resource Group
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Last Updated
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {containerapps.map((app: { id: string; status: string; lastUpdated: string | number | Date; name: string; resourceGroup:string }) => (
                                <tr key={app.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">{app.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <StatusBadge status={app.status} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500 dark:text-gray-400">{app.resourceGroup}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(app.lastUpdated).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* System Messages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">System Messages</h2>
                </div>
                <div className="p-4">
                    {alerts.length > 0 ? (
                        <ul className="space-y-3">
                            {alerts.filter((alert: { resolved: any; }) => !alert.resolved).slice(0, 5).map((alert: { id: string; severity: string; title: string ; timestamp: string | number | Date; message: string; }) => (
                                <li key={alert.id} className={`p-3 rounded-md ${alert.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/30' :
                                        alert.severity === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/30' :
                                            'bg-blue-50 dark:bg-blue-900/30'
                                    }`}>
                                    <div className="flex justify-between">
                                        <p className={`text-sm font-medium ${alert.severity === 'critical' ? 'text-red-800 dark:text-red-200' :
                                                alert.severity === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                                                    'text-blue-800 dark:text-blue-200'
                                            }`}>
                                            {alert.title}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {new Date(alert.timestamp).toLocaleString()}
                                        </p>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{alert.message}</p>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-gray-600 dark:text-gray-400">No active system messages.</p>
                    )}
                </div>
            </div>
        </div>
    );
};
export default StatusDashboardPage;